import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, Download, FileSignature } from 'lucide-react';
import {
  CHECK_STAGE_LABEL,
  type CheckStage,
  type OnboardingTaskKind,
  type FormProgress,
  type PreflightKey,
  type TrainingTaskSummary,
} from '@hcc/types';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardSection } from '../ui/Card';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { Pill } from '../ui/Pill';
import { LinkifiedText } from '../ui/LinkifiedText';
import { CHECK_STAGE_TONE } from './CheckTaskRow';

/**
 * The inductee's onboarding to-do list. Every row is a compact summary
 * (title, status, one action button) that navigates to a dedicated,
 * focused page: forms to /me/forms/:taskId, documents to /me/sign/:taskId,
 * and to-dos / uploads / screening checks to /me/tasks/:taskId. Nothing
 * is actioned inline; the list stays scannable.
 *
 * Training modules are onboarding tasks too, but they render in their own
 * section (see components/training/TrainingTasks) — doing a course is not
 * paperwork, and counting the two together made this list look far longer
 * than the paperwork actually is.
 */

export interface OnboardingTask {
  task_id: string;
  title: string;
  description: string | null;
  status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
  rejection_reason: string | null;
  onboarding_kind?: OnboardingTaskKind;
  document_id?: string;
  form_id?: string;
  /** Screening-check stage, present when onboarding_kind === 'check'. */
  check_stage?: CheckStage;
  /** Pre-employment gate this task satisfies, when set. */
  preflight_key?: PreflightKey;
  /** In-progress completion for a started-but-unsubmitted form. */
  progress?: FormProgress | null;
  /** Attempt standing, present when onboarding_kind === 'training'. */
  training?: TrainingTaskSummary | null;
}

export function statusPill(status: OnboardingTask['status']) {
  switch (status) {
    case 'complete':
      return <Pill tone="success" icon={<CheckCircle2 className="h-3 w-3" />}>Done</Pill>;
    case 'awaiting_review':
      return <Pill tone="info">Awaiting review</Pill>;
    case 'changes_requested':
      return <Pill tone="warning">Changes needed</Pill>;
    default:
      return <Pill tone="neutral">To do</Pill>;
  }
}

/** Whole-number percent of required (visible) fields done, or null if none. */
function progressPct(p: FormProgress): number | null {
  if (p.required_total === 0) return null;
  return Math.round((p.required_done / p.required_total) * 100);
}

export function OnboardingTasks({
  tasks,
  startDate,
}: {
  tasks: OnboardingTask[];
  /** The person's start date (YYYY-MM-DD), for the all-done copy. */
  startDate?: string | null;
}) {
  const onboarding = tasks.filter((t) => t.onboarding_kind && t.onboarding_kind !== 'training');
  if (onboarding.length === 0) return null;

  // From the inductee's perspective a submitted-and-awaiting-review task is
  // off their plate, so it counts as done here. (changes_requested is back
  // with them, so it doesn't.) The HR "Onboarding status" card counts only
  // fully-approved tasks.
  const done = onboarding.filter(
    (t) => t.status === 'complete' || t.status === 'awaiting_review',
  ).length;

  const rows = (
    <div className="divide-y divide-divider/10">
      {onboarding.map((t) => (
        <OnboardingTaskRow key={t.task_id} task={t} />
      ))}
    </div>
  );

  // Nothing left to action: minimise the section to a congratulatory
  // line. New tasks land as 'pending', so the full list reappears by
  // itself the moment there is something to do (and the new-task email
  // makes good on the "we'll let you know" promise). Expanding still
  // shows every row, e.g. to re-download a signed document.
  if (done === onboarding.length) {
    const awaiting = onboarding.filter((t) => t.status === 'awaiting_review').length;
    // slice(0, 10) tolerates both 'YYYY-MM-DD' and a full ISO timestamp.
    const startMs = startDate
      ? new Date(startDate.slice(0, 10) + 'T00:00:00').getTime()
      : NaN;
    const beforeStart = !Number.isNaN(startMs) && startMs > Date.now();
    const message = [
      `You're all done${beforeStart ? ' before your start date' : ''}. We'll let you know if there's anything more to do.`,
      awaiting > 0 ? `${awaiting} task(s) are with HR for review.` : null,
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <CollapsibleSection
        icon={<CheckCircle2 className="h-4 w-4 text-success" />}
        title="Onboarding complete"
        description={message}
        trailing={`${done} of ${onboarding.length}`}
      >
        {rows}
      </CollapsibleSection>
    );
  }

  return (
    <Card>
      <CardHeader
        icon={<ChevronRight className="h-4 w-4" />}
        title="Your onboarding"
        description={`${done} of ${onboarding.length} complete`}
      />
      {rows}
    </Card>
  );
}

function OnboardingTaskRow({ task }: { task: OnboardingTask }) {
  if (task.onboarding_kind === 'document_ack') return <DocumentSignRow task={task} />;
  if (task.onboarding_kind === 'form') return <FormTaskRow task={task} />;
  if (
    task.onboarding_kind === 'upload' ||
    task.onboarding_kind === 'todo' ||
    task.onboarding_kind === 'check'
  ) {
    return <LinkTaskRow task={task} />;
  }

  // Legacy capture kinds no longer have an in-app form. Show status only.
  return (
    <CardSection>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-fg">{task.title}</div>
          {task.description ? <p className="mt-0.5 text-sm text-fg-muted">{task.description}</p> : null}
        </div>
        {statusPill(task.status)}
      </div>
    </CardSection>
  );
}

/**
 * Row for a configurable form task. Opening it navigates to a dedicated,
 * focused page (/me/forms/:taskId) rather than expanding inline, so the
 * inductee works through the whole form on its own screen.
 */
function FormTaskRow({ task }: { task: OnboardingTask }) {
  // A pending form the inductee has begun (autosaved draft) reads as "in
  // progress" with a percentage, so resuming it doesn't look like a fresh start.
  const started = task.status === 'pending' && !!task.progress?.started;
  const pct = task.progress ? progressPct(task.progress) : null;
  // Editable while pending or after changes were requested; otherwise (awaiting
  // review / complete) it's read-only and the inductee can View what they sent.
  const label =
    task.status === 'pending'
      ? started
        ? 'Resume'
        : 'Start'
      : task.status === 'changes_requested'
        ? 'Fix'
        : 'View';
  // Make the primary action pop: green to start a fresh form, amber to resume a
  // part-filled one. Read-only states (Fix / View) stay subtle.
  const variant: 'success' | 'warning' | 'ghost' =
    task.status === 'pending' ? (started ? 'warning' : 'success') : 'ghost';

  return (
    <CardSection>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-fg">{task.title}</div>
          {task.description ? <p className="mt-0.5 text-sm text-fg-muted">{task.description}</p> : null}
          {task.status === 'changes_requested' && task.rejection_reason ? (
            <p className="mt-1 text-sm text-warning-soft-fg">{task.rejection_reason}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {started ? (
            <Pill tone="warning">In progress{pct !== null ? ` · ${pct}%` : ''}</Pill>
          ) : (
            statusPill(task.status)
          )}
          <Link to={`/me/forms/${task.task_id}`}>
            <Button size="sm" variant={variant}>
              {label}
            </Button>
          </Link>
        </div>
      </div>
    </CardSection>
  );
}

/**
 * Compact row for a to-do, an upload, or a screening check. The action
 * happens on the dedicated /me/tasks/:taskId page; this row just shows
 * the status and gets the inductee there. Check tasks deliberately hide
 * their description here: it's the full application instructions, which
 * belong on the focused page, not in the list.
 */
function LinkTaskRow({ task }: { task: OnboardingTask }) {
  const isCheck = task.onboarding_kind === 'check';
  const stage: CheckStage = task.check_stage ?? 'not_applied';
  const actionable = isCheck
    ? stage === 'not_applied' || stage === 'changes_requested'
    : task.status === 'pending' || task.status === 'changes_requested';
  const needsFix = isCheck
    ? stage === 'changes_requested'
    : task.status === 'changes_requested';
  const label = actionable ? (needsFix ? 'Fix' : 'Start') : 'View';
  const variant: 'success' | 'warning' | 'ghost' = !actionable
    ? 'ghost'
    : needsFix
      ? 'warning'
      : 'success';

  return (
    <CardSection>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-fg">{task.title}</div>
          {!isCheck && task.description ? (
            <p className="mt-0.5 text-sm text-fg-muted">
              <LinkifiedText text={task.description} />
            </p>
          ) : null}
          {needsFix && task.rejection_reason ? (
            <p className="mt-1 text-sm text-warning-soft-fg">{task.rejection_reason}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isCheck ? (
            <Pill tone={CHECK_STAGE_TONE[stage]}>{CHECK_STAGE_LABEL[stage]}</Pill>
          ) : (
            statusPill(task.status)
          )}
          <Link to={`/me/tasks/${task.task_id}`}>
            <Button size="sm" variant={variant}>
              {label}
            </Button>
          </Link>
        </div>
      </div>
    </CardSection>
  );
}

/**
 * Row for a read-and-sign document task. Opening it navigates to a dedicated,
 * focused page (/me/sign/:taskId) - mirroring the form flow - so the inductee
 * gets the full screen for the PDF instead of a cramped inline iframe.
 */
function DocumentSignRow({ task }: { task: OnboardingTask }) {
  const locked = task.status === 'complete' || task.status === 'awaiting_review';
  return (
    <CardSection>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-fg">{task.title}</div>
          {task.description ? <p className="mt-0.5 text-sm text-fg-muted">{task.description}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {statusPill(task.status)}
          {locked ? (
            <a
              href={`/api/me/onboarding/documents/${task.task_id}/signed.pdf`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          ) : (
            <Link to={`/me/sign/${task.task_id}`}>
              <Button size="sm" variant="success" iconLeft={<FileSignature className="h-4 w-4" />}>
                Review &amp; sign
              </Button>
            </Link>
          )}
        </div>
      </div>
    </CardSection>
  );
}
